import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import type { Session } from '@supabase/supabase-js'
import { AuthContext, type AuthContextValue } from '@/auth/context'
import { consumePendingInviteLanding } from '@/auth/inviteLanding'
import { resetMeCache } from '@/auth/useMe'
import { supabase } from '@/lib/supabase'

const SET_PASSWORD_PATH = '/wachtwoord-instellen'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false

    // An invite/recovery link just landed with a one-time token (already being
    // exchanged for a session by detectSessionInUrl). Send the user to the
    // set-password screen so they can pick a password. Consuming the flag
    // clears it, so later effect re-runs (navigate's identity changes on every
    // location change) cannot trap the user on the set-password screen.
    if (consumePendingInviteLanding()) {
      void navigate(SET_PASSWORD_PATH, { replace: true })
    }

    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) {
        setSession(data.session)
        setLoading(false)
      }
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, newSession) => {
      setSession(newSession)
      setLoading(false)
      // Supabase emits this when a recovery link is followed; route to the
      // set-password screen even if the hash was cleared before we read it.
      if (event === 'PASSWORD_RECOVERY') {
        void navigate(SET_PASSWORD_PATH, { replace: true })
      }
    })

    return () => {
      cancelled = true
      subscription.unsubscribe()
    }
  }, [navigate])

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    return { error: error ? error.message : null }
  }, [])

  const signInWithMagicLink = useCallback(
    async (email: string, redirectPath?: string) => {
      // Compose an absolute redirect URL from the current origin so the magic
      // link returns the user to this deployment (works on staging + prod). The
      // path defaults to the app root; founders get their form path here.
      const emailRedirectTo =
        typeof window !== 'undefined'
          ? new URL(redirectPath ?? '/', window.location.origin).toString()
          : undefined
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: emailRedirectTo ? { emailRedirectTo } : undefined,
      })
      return { error: error ? error.message : null }
    },
    [],
  )

  const signOut = useCallback(async () => {
    // Drop the cached /me profile so the next session re-fetches its own.
    resetMeCache()
    await supabase.auth.signOut()
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({ session, loading, signIn, signInWithMagicLink, signOut }),
    [session, loading, signIn, signInWithMagicLink, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
